import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    OnDestroy,
    ViewChild,
    inject,
    signal,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

import { AlertService } from '@shared/services/alert.service';
import { ZDialogComponent } from '../../../../../shared/components/z-dialog/z-dialog.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { ZButtonComponent } from '../../../../../shared/components/z-button/z-button.component';

type Step = 'camera' | 'crop';

const CROP_OUTPUT_SIZE = 512;

@Component({
    selector: 'app-camera-capture-dialog',
    templateUrl: './camera-capture-dialog.component.html',
    styleUrls: ['./camera-capture-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ZDialogComponent,
        MatProgressSpinner,
        MatIcon,
        ZButtonComponent,
        TranslatePipe,
    ],
})
export class CameraCaptureDialogComponent implements AfterViewInit, OnDestroy {
    private readonly alertService = inject(AlertService);
    private readonly translate = inject(TranslateService);
    private readonly dialogRef = inject(MatDialogRef<CameraCaptureDialogComponent, File | null>);
    private readonly cdr = inject(ChangeDetectorRef);

    @ViewChild('videoEl') videoEl?: ElementRef<HTMLVideoElement>;
    @ViewChild('cropCanvasEl') cropCanvasEl?: ElementRef<HTMLCanvasElement>;

    readonly step = signal<Step>('camera');
    readonly initializing = signal<boolean>(true);

    readonly minZoom = signal<number>(1);
    readonly maxZoom = signal<number>(4);
    readonly zoom = signal<number>(1);

    private stream: MediaStream | null = null;
    private capturedImage: HTMLImageElement | null = null;
    private capturedUrl: string | null = null;

    private offsetX = 0;
    private offsetY = 0;
    private dragging = false;
    private dragStartX = 0;
    private dragStartY = 0;
    private dragStartOffsetX = 0;
    private dragStartOffsetY = 0;

    private get cropSize(): number {
        return this.cropCanvasEl?.nativeElement.width ?? 0;
    }

    ngAfterViewInit(): void {
        this.startCamera();
    }

    ngOnDestroy(): void {
        this.stopCamera();
        if (this.capturedUrl) URL.revokeObjectURL(this.capturedUrl);
    }

    private async startCamera(): Promise<void> {
        if (!navigator.mediaDevices?.getUserMedia) {
            this.alertService.showErrorMessage(
                this.translate.instant('HEADER.USER_MENU.CAMERA_NOT_SUPPORTED'),
            );
            this.dialogRef.close(null);
            return;
        }

        try {
            this.stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
                audio: false,
            });
            const video = this.videoEl!.nativeElement;
            video.srcObject = this.stream;
            await video.play();
            this.initializing.set(false);
        } catch {
            this.alertService.showErrorMessage(
                this.translate.instant('HEADER.USER_MENU.CAMERA_PERMISSION_DENIED'),
            );
            this.dialogRef.close(null);
        }
    }

    private stopCamera(): void {
        this.stream?.getTracks().forEach((track) => track.stop());
        this.stream = null;
    }

    capture(): void {
        const video = this.videoEl!.nativeElement;
        const off = document.createElement('canvas');
        off.width = video.videoWidth;
        off.height = video.videoHeight;
        const ctx = off.getContext('2d');
        if (!ctx) return;
        ctx.translate(off.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(video, 0, 0, off.width, off.height);

        off.toBlob((blob) => {
            if (!blob) return;
            if (this.capturedUrl) URL.revokeObjectURL(this.capturedUrl);
            this.capturedUrl = URL.createObjectURL(blob);
            const img = new Image();
            img.onload = () => {
                this.capturedImage = img;
                this.stopCamera();
                this.step.set('crop');
                this.cdr.detectChanges();
                this.initCrop();
            };
            img.src = this.capturedUrl;
        }, 'image/jpeg', 0.92);
    }

    private initCrop(): void {
        const canvas = this.cropCanvasEl!.nativeElement;
        const size = canvas.clientWidth;
        canvas.width = size;
        canvas.height = size;

        const img = this.capturedImage!;
        const baseScale = Math.max(size / img.width, size / img.height);
        this.minZoom.set(1);
        this.maxZoom.set(4);
        this.zoom.set(1);

        const scaledW = img.width * baseScale * this.zoom();
        const scaledH = img.height * baseScale * this.zoom();
        this.offsetX = (size - scaledW) / 2;
        this.offsetY = (size - scaledH) / 2;

        this.drawCrop();
    }

    private drawCrop(): void {
        const canvas = this.cropCanvasEl!.nativeElement;
        const ctx = canvas.getContext('2d');
        if (!ctx || !this.capturedImage) return;

        const size = canvas.width;
        const img = this.capturedImage;
        const baseScale = Math.max(size / img.width, size / img.height);
        const scale = baseScale * this.zoom();
        const drawW = img.width * scale;
        const drawH = img.height * scale;

        this.clampOffsets(drawW, drawH, size);

        ctx.clearRect(0, 0, size, size);
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(img, this.offsetX, this.offsetY, drawW, drawH);

        ctx.save();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.beginPath();
        ctx.rect(0, 0, size, size);
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2, true);
        ctx.fill('evenodd');
        ctx.restore();

        ctx.save();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2 - 1, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
    }

    private clampOffsets(drawW: number, drawH: number, size: number): void {
        const minX = size - drawW;
        const minY = size - drawH;
        if (this.offsetX > 0) this.offsetX = 0;
        if (this.offsetX < minX) this.offsetX = minX;
        if (this.offsetY > 0) this.offsetY = 0;
        if (this.offsetY < minY) this.offsetY = minY;
    }

    onZoomChange(event: Event): void {
        const value = parseFloat((event.target as HTMLInputElement).value);
        const canvas = this.cropCanvasEl!.nativeElement;
        const size = canvas.width;
        const img = this.capturedImage!;
        const baseScale = Math.max(size / img.width, size / img.height);

        const oldScale = baseScale * this.zoom();
        const newScale = baseScale * value;
        const cx = size / 2;
        const cy = size / 2;
        const imgX = (cx - this.offsetX) / oldScale;
        const imgY = (cy - this.offsetY) / oldScale;
        this.offsetX = cx - imgX * newScale;
        this.offsetY = cy - imgY * newScale;
        this.zoom.set(value);
        this.drawCrop();
    }

    onPointerDown(event: PointerEvent): void {
        const canvas = this.cropCanvasEl!.nativeElement;
        canvas.setPointerCapture(event.pointerId);
        this.dragging = true;
        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
        this.dragStartOffsetX = this.offsetX;
        this.dragStartOffsetY = this.offsetY;
    }

    onPointerMove(event: PointerEvent): void {
        if (!this.dragging) return;
        this.offsetX = this.dragStartOffsetX + (event.clientX - this.dragStartX);
        this.offsetY = this.dragStartOffsetY + (event.clientY - this.dragStartY);
        this.drawCrop();
    }

    onPointerUp(event: PointerEvent): void {
        const canvas = this.cropCanvasEl!.nativeElement;
        if (canvas.hasPointerCapture(event.pointerId)) {
            canvas.releasePointerCapture(event.pointerId);
        }
        this.dragging = false;
    }

    @HostListener('window:resize')
    onResize(): void {
        if (this.step() === 'crop') {
            this.initCrop();
        }
    }

    retake(): void {
        if (this.capturedUrl) {
            URL.revokeObjectURL(this.capturedUrl);
            this.capturedUrl = null;
        }
        this.capturedImage = null;
        this.step.set('camera');
        this.initializing.set(true);
        this.cdr.detectChanges();
        this.startCamera();
    }

    confirm(): void {
        if (!this.capturedImage) return;

        const canvas = this.cropCanvasEl!.nativeElement;
        const size = canvas.width;
        const img = this.capturedImage;
        const baseScale = Math.max(size / img.width, size / img.height);
        const scale = baseScale * this.zoom();

        const sx = -this.offsetX / scale;
        const sy = -this.offsetY / scale;
        const sSize = size / scale;

        const out = document.createElement('canvas');
        out.width = CROP_OUTPUT_SIZE;
        out.height = CROP_OUTPUT_SIZE;
        const ctx = out.getContext('2d');
        if (!ctx) return;

        ctx.save();
        ctx.beginPath();
        ctx.arc(CROP_OUTPUT_SIZE / 2, CROP_OUTPUT_SIZE / 2, CROP_OUTPUT_SIZE / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(
            img,
            sx, sy, sSize, sSize,
            0, 0, CROP_OUTPUT_SIZE, CROP_OUTPUT_SIZE,
        );
        ctx.restore();

        out.toBlob((blob) => {
            if (!blob) return;
            const file = new File([blob], `profile-${Date.now()}.png`, { type: 'image/png' });
            this.dialogRef.close(file);
        }, 'image/png');
    }

    cancel(): void {
        this.dialogRef.close(null);
    }
}
